/**
 * TOSS Web Application - Header Component
 *
 * The internal application's top bar. Since 2026-08-26 it is the shared `TopBar`
 * (`components/topbar/**`) with the internal audience's controls in its slots — the customer
 * portal draws the same frame with its own. What each surface may put there is decided on the
 * surface; the frame knows nothing about either.
 *
 * Left: the drawer toggle on a phone, and nothing else.
 *
 * **There is no page title here, and it is not a removal — it never printed one.** The `<h1>`
 * read `uiStore.pageTitle`, which is written only by `AppLayout` from a `pageTitle` prop that
 * no caller has ever passed: `grep pageTitle=` finds the prop's own declaration and two
 * docblock examples. Pages name themselves through `PageHeader` (30 of them do), which is what
 * the owner asked the portal to copy — so the empty slot went away rather than being filled.
 * The same audit removed `showSearch`, whose input was a placeholder that no caller enabled,
 * and `actions`, which nothing passed either.
 *
 * Right: clocks, a rule, the tools as one group (the equipment-term button, the calculator, the
 * messenger), then the bell — outside the group and always last, because it reports a state
 * rather than opening a utility.
 *
 * **The group is the same on every screen of the application** (owner, 26.08.2026, looking at the
 * messenger). It used to thin out to one icon there: the tools were gated on `showTools`, which
 * `AppLayout` passed as `!fullHeight`, and the messenger is the only route with that layout. The
 * reasons were real and are both answered now — the calculator panel renders on that route too, so
 * its button is no longer dead, and the messenger button lights up instead of pretending to lead
 * somewhere when you are already there. A bar that loses two controls on one screen out of nine
 * reads as a bug, not as a decision.
 *
 * The phone still has no tools: the drawer and the floating messenger button are its way to the
 * same places, and the calculator panel is `hidden lg:flex` by its own class.
 */

import { forwardRef, useRef, type HTMLAttributes } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Bell, Menu, MessagesSquare, Calculator } from 'lucide-react';
import { useUIStore, useCalculatorPanel } from '@/store/uiStore';
import { useResponsive } from '@/hooks/useResponsive';
import { useUnreadCount } from '@/hooks/api/useNotifications';
import { useMessengerUnreadCount } from '@/hooks/api/useMessenger';
import { NotificationsPanel } from '@/features/notifications/components';
import {
  TimeZoneClock,
  TopBar,
  TopBarAction,
  TopBarActionGroup,
  TopBarDivider,
} from '@/components/topbar';
import { ConnectionStatusThread } from '@/components/ui/ConnectionThread';
import { EquipmentTermPopover } from './EquipmentTermPopover';

// ============================================================================
// Types
// ============================================================================

export interface HeaderProps extends HTMLAttributes<HTMLElement> {
  /** Полоса или карточка — см. `TopBar`. Решает оболочка: на телефоне полоса, на компьютере карточка. */
  variant?: 'bar' | 'card';
  /** Additional class names */
  className?: string;
}

// ============================================================================
// Sub-components
// ============================================================================

/**
 * Notification bell with badge and dropdown panel.
 *
 * The badge counts since 2026-08-26; it used to be a bare dot, which told a dispatcher that
 * something had arrived but not whether it was one approval or forty — while the messenger
 * button two icons away had been printing its number all along.
 *
 * Since 24.09.2026 it counts the PRIORITY sector only (agreement with the customer): a client's
 * application, «осмотр не выполнен», a repair handed to the next ПТО, unclosed repairs. Everything
 * else waits in the panel under a primary dot and does not light the bell.
 */
function NotificationBell(): JSX.Element {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const toggleNotificationsPanel = useUIStore((state) => state.toggleNotificationsPanel);
  const { data: unreadData } = useUnreadCount();

  return (
    <div className="relative overflow-visible">
      <TopBarAction
        ref={triggerRef}
        icon={Bell}
        label="Уведомления"
        badge={unreadData?.priorityCount ?? 0}
        onClick={toggleNotificationsPanel}
      />
      {/* Notifications dropdown panel - rendered via Portal */}
      <NotificationsPanel triggerRef={triggerRef} />
    </div>
  );
}

/**
 * Messenger quick-access button with unread badge.
 * Moved here from the old bottom-right floating toolbar.
 */
function MessengerButton(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: messengerUnread } = useMessengerUnreadCount();
  // Lit rather than hidden while you are in the messenger: the button is where the eye looks for
  // «где я», and a control that vanishes on its own screen is what made the bar there look broken.
  const isActive = location.pathname.startsWith('/messenger');

  return (
    <TopBarAction
      icon={MessagesSquare}
      label="Мессенджер"
      badge={messengerUnread?.count ?? 0}
      active={isActive}
      onClick={() => navigate('/messenger')}
    />
  );
}

/**
 * Calculator toggle button — opens/closes the right-side CalculatorPanel.
 * Moved here from the old bottom-right floating toolbar.
 */
function CalculatorButton(): JSX.Element {
  const { isOpen, toggle } = useCalculatorPanel();

  return (
    <TopBarAction
      icon={Calculator}
      label={isOpen ? 'Закрыть калькулятор' : 'Открыть калькулятор'}
      title={isOpen ? 'Закрыть калькулятор' : 'Калькулятор'}
      active={isOpen}
      onClick={toggle}
    />
  );
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * Header component for the application
 *
 * @example
 * ```tsx
 * <Header />
 * ```
 */
export const Header = forwardRef<HTMLElement, HeaderProps>(
  ({ className = '', variant, ...props }, ref) => {
    const { showMobileNav } = useResponsive();
    const toggleMobileMenu = useUIStore((state) => state.toggleMobileMenu);

    return (
      <TopBar
        variant={variant}
        ref={ref}
        className={className}
        leading={
          showMobileNav ? (
            <TopBarAction icon={Menu} label="Открыть меню" onClick={toggleMobileMenu} />
          ) : undefined
        }
        actions={
          <>
            <TimeZoneClock />
            <TopBarDivider />

            {/* The tools as one object. The term button is in it at every width — a phone renames
                the thing as often as a desktop does. The other two are for wide screens: the
                calculator opens a 320 px column, and the phone reaches the messenger through its
                floating button. */}
            <TopBarActionGroup>
              {/* Equipment term meme button — customer keeps renaming this thing */}
              <EquipmentTermPopover />
              {!showMobileNav && <CalculatorButton />}
              {!showMobileNav && <MessengerButton />}
            </TopBarActionGroup>

            <NotificationBell />
          </>
        }
        underline={<ConnectionStatusThread />}
        {...props}
      />
    );
  }
);

Header.displayName = 'Header';

export default Header;
